import { useCallback, useEffect, useState } from "react";
import { useZero } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";

interface ThreadState {
  channelId: string | null;
  threadId: string | null;
}

interface UseThreadRoutingArgs {
  channelId: string | null;
  targetMessageId?: string | null;
  targetThreadId?: string | null;
  onTargetMessageHandled?: () => void;
  setThreadState: React.Dispatch<React.SetStateAction<ThreadState>>;
  /**
   * Fired when the notification intent resolves to a top-level
   * (non-thread) message. The parent forwards into the channel-level
   * scroll request flow.
   */
  setScrollRequestForNotification: (messageId: string) => void;
  zero: ReturnType<typeof useZero>;
}

/**
 * Owns the notification deep-link routing for `MessageArea`:
 *
 * - If `targetThreadId` is explicitly present, route into the thread
 *   panel (with `targetMessageId` as the reply to scroll to, if
 *   provided).
 * - Otherwise, if only `targetMessageId` is present, look up the
 *   message and route into the thread panel when its `thread_id` is
 *   non-null (legacy notifications predating migration 070). Fall
 *   through to channel-level scroll for top-level messages.
 *
 * Bypasses `setActiveThread`'s toggle behaviour — if the panel is
 * already open on this thread, a notification click should keep it
 * open and just re-scroll, not close it.
 *
 * Also exposes `threadScrollTargetId` + `handleThreadScrollHandled`
 * for the thread panel's own scroll plumbing.
 */
export function useThreadRouting({
  channelId,
  targetMessageId,
  targetThreadId,
  onTargetMessageHandled,
  setThreadState,
  setScrollRequestForNotification,
  zero,
}: UseThreadRoutingArgs) {
  // Reply-id to scroll to INSIDE the thread panel. Local to MessageArea so
  // we can clear the parent's targetMessageId immediately (avoiding a re-entry
  // loop) while the panel consumes this on its own timeline.
  const [threadScrollTargetId, setThreadScrollTargetId] = useState<
    string | null
  >(null);

  const handleThreadScrollHandled = useCallback(() => {
    setThreadScrollTargetId(null);
  }, []);

  // Resolve thread routing from notification intent.
  useEffect(() => {
    if (!targetMessageId && !targetThreadId) return;
    let cancelled = false;

    const routeToThread = (threadId: string, replyId: string | null) => {
      // Bypass setActiveThread's toggle behaviour — if the panel is already
      // open on this thread, a notification click should keep it open and
      // just re-scroll, not close it.
      setThreadState({ channelId, threadId });
      setThreadScrollTargetId(replyId);
      onTargetMessageHandled?.();
    };

    if (targetThreadId) {
      routeToThread(targetThreadId, targetMessageId ?? null);
      return;
    }

    // Legacy fallback: look up the message and check thread_id.
    if (!targetMessageId) return;
    void (async () => {
      try {
        const msg = await zero.run(queries.messages.byId(targetMessageId));
        if (cancelled) return;
        const parentId = (msg as { thread_id?: string | null } | null)
          ?.thread_id;
        if (parentId) {
          routeToThread(parentId, targetMessageId);
        }
      } catch {
        // Swallow — channel-scroll path handles the toast if it truly can't find the message
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    targetMessageId,
    targetThreadId,
    channelId,
    onTargetMessageHandled,
    setThreadState,
    zero,
  ]);

  // Channel-level scroll request — only fires for top-level messages. Skipped
  // when the intent was already routed into the thread panel above.
  useEffect(() => {
    if (!targetMessageId) return;
    if (targetThreadId) return; // thread path handles this
    setScrollRequestForNotification(targetMessageId);
  }, [targetMessageId, targetThreadId, setScrollRequestForNotification]);

  return { threadScrollTargetId, handleThreadScrollHandled };
}
